import { IGenaiUsageData, IGenaiUsageEntry } from "../models/interfaces";
import { GovernanceEntry, GovernanceSummary } from "./types";

/**
 * BE sometimes emits naive ISO datetimes for resets_at (no Z/offset). Treat those as UTC
 * so downstream parsers (Date.parse / dayjs) don't reinterpret them as local time.
 */
const normalizeIsoUtc = (s: string | undefined | null): string => {
	if (!s) return s ?? "";
	if (/Z$|[+-]\d{2}:?\d{2}$/.test(s)) return s;
	return `${s}Z`;
};

const toEntry = (e: IGenaiUsageEntry): GovernanceEntry => ({
	window: e.window,
	pctUsed: typeof e.pct_used === "number" ? e.pct_used : Number(e.pct_used) || 0,
	status: e.status,
	resetsAt: normalizeIsoUtc(e.resets_at),
	// BE now emits one entry per (window, metric) pair. Older deploys omit
	// `metric` entirely — default to "dollars" so the legacy single-axis
	// behavior is preserved.
	metric: e.metric ?? "dollars",
});

/**
 * Translates a raw /masters/genai-usage payload into the FE-internal model.
 * This is the only file in the FE that knows the raw BE shape; everything
 * downstream consumes GovernanceSummary.
 */
export const adaptGenaiUsage = (data: IGenaiUsageData | null | undefined): GovernanceSummary => {
	if (!data) {
		return { mode: "off", warningThresholdPct: 0, usage: [] };
	}
	return {
		mode: data.mode,
		warningThresholdPct: typeof data.warning_threshold_pct === "number" ? data.warning_threshold_pct : Number(data.warning_threshold_pct) || 0,
		usage: Array.isArray(data.usage) ? data.usage.map(toEntry) : [],
	};
};
